// src/components/Lightbox.jsx
import { useEffect, useRef, useState } from 'react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const CLICK_ZOOM = 2.5;
const WHEEL_SENSITIVITY = 0.0015;

// images: [{ src, alt }] · index: position ouverte dans le tableau (null = fermé)
export default function Lightbox({ images, index, onClose, onNavigate }) {
    const isOpen = index !== null && index !== undefined && images?.[index];
    const canNavigate = isOpen && images.length > 1;

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null); // { startX, startY, panX, panY } pendant un drag
    const didDragRef = useRef(false); // évite qu'un relâchement après glisser ne déclenche le clic

    // Le zoom/pan repart de zéro à chaque changement d'image ou de fermeture.
    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [index]);

    useEffect(() => {
        if (!isOpen) return;

        const goNext = () => onNavigate((index + 1) % images.length);
        const goPrev = () => onNavigate((index - 1 + images.length) % images.length);

        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (canNavigate && e.key === 'ArrowRight') goNext();
            if (canNavigate && e.key === 'ArrowLeft') goPrev();
        };

        window.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, canNavigate, index, images, onClose, onNavigate]);

    if (!isOpen) return null;

    const { src, alt } = images[index];

    // Clic sur l'image : zoome vers le point cliqué, ou revient à l'ajustement automatique.
    const onImageClick = (e) => {
        e.stopPropagation();
        if (didDragRef.current) {
            didDragRef.current = false;
            return;
        }
        if (zoom > 1) {
            setZoom(1);
            setPan({ x: 0, y: 0 });
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = (rect.width / 2 - (e.clientX - rect.left)) * (CLICK_ZOOM - 1);
        const offsetY = (rect.height / 2 - (e.clientY - rect.top)) * (CLICK_ZOOM - 1);
        setZoom(CLICK_ZOOM);
        setPan({ x: offsetX, y: offsetY });
    };

    // Molette : zoom progressif en pourcentage, centré sur le curseur.
    const onWheel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom - e.deltaY * WHEEL_SENSITIVITY * zoom));

        if (nextZoom === 1) {
            setZoom(1);
            setPan({ x: 0, y: 0 });
            return;
        }

        const cursorX = rect.width / 2 - (e.clientX - rect.left);
        const cursorY = rect.height / 2 - (e.clientY - rect.top);
        const ratio = nextZoom / zoom;
        setPan((p) => ({
            x: cursorX - (cursorX - p.x) * ratio,
            y: cursorY - (cursorY - p.y) * ratio
        }));
        setZoom(nextZoom);
    };

    const onDragStart = (e) => {
        if (zoom <= 1) return;
        e.stopPropagation();
        const point = e.touches ? e.touches[0] : e;
        dragRef.current = { startX: point.clientX, startY: point.clientY, panX: pan.x, panY: pan.y };
        didDragRef.current = false;
    };

    const onDragMove = (e) => {
        if (!dragRef.current) return;
        e.stopPropagation();
        const point = e.touches ? e.touches[0] : e;
        const { startX, startY, panX, panY } = dragRef.current;
        if (Math.abs(point.clientX - startX) > 4 || Math.abs(point.clientY - startY) > 4) {
            didDragRef.current = true;
        }
        setPan({ x: panX + (point.clientX - startX), y: panY + (point.clientY - startY) });
    };

    const onDragEnd = (e) => {
        if (!dragRef.current) return;
        e.stopPropagation();
        dragRef.current = null;
    };

    return (
        <div className="lightbox" onClick={onClose}>
            <button className="lightbox-close" onClick={onClose} aria-label="Fermer">×</button>

            {zoom > 1 && <div className="lightbox-zoom-badge">{Math.round(zoom * 100)}%</div>}

            {canNavigate && (
                <button
                    className="lightbox-nav lightbox-prev"
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate((index - 1 + images.length) % images.length);
                    }}
                    aria-label="Image précédente"
                >
                    ‹
                </button>
            )}

            <div
                className="lightbox-frame"
                onWheel={onWheel}
                onMouseDown={onDragStart}
                onMouseMove={onDragMove}
                onMouseUp={onDragEnd}
                onMouseLeave={onDragEnd}
                onTouchStart={onDragStart}
                onTouchMove={onDragMove}
                onTouchEnd={onDragEnd}
            >
                <img
                    src={src}
                    alt={alt}
                    onClick={onImageClick}
                    draggable={false}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        cursor: zoom > 1 ? 'grab' : 'zoom-in'
                    }}
                />
            </div>

            {canNavigate && (
                <button
                    className="lightbox-nav lightbox-next"
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate((index + 1) % images.length);
                    }}
                    aria-label="Image suivante"
                >
                    ›
                </button>
            )}
        </div>
    );
}
